import * as React from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useGlobalContext } from '../state/GlobalStateProvider';
import { Hamburger, Checkbox, Text, Label, Link as FluentLink, makeStyles, shorthands, useId, Persona, Popover, PopoverTrigger, PopoverSurface, Button, OpenPopoverEvents, OnOpenChangeData, MenuButton, tokens } from '@fluentui/react-components';
import { FluentDialog } from './FluentDialog';
import { formInputInlineStyle, formInputStyle, formStyle, infoLabel } from '../styles/GlobalStyles';
import { UserSettingsDialog } from './UserSettingsDialog';
import { ClientPrincipal } from '../util/ClientPrincipal';
import { mobileMaxWidth, useMediaQuery } from '../util/ResponsiveDesign';
import { ensureSuccess, handleError } from '../util/ErrorHandling';
import { ErrorDialog } from './ErrorDialog';
import { UserRecord } from '../dto/User';
import { RetrieveMultipleResponse } from '../dto/RetrieveMultipleResponse';
import { Publisher } from '../dto/Publisher';

const getUserInfo = async (): Promise<ClientPrincipal | null> => {
    try {
        const response = await fetch('/.auth/me');

        if (response.ok)
        {
            const payload = await response.json();
            const { clientPrincipal } = payload;
            return clientPrincipal;
        }
        else
        {
            return null;
        }
    }
    catch (error) {
        console.error('No profile could be found');
        return null;
    }
};

const useStyles = makeStyles({
    root: {
        fontWeight: "800",
        width: "90%",
        ...shorthands.margin("0", "auto")
    },
    header: {
        position: "sticky",
        top: 0,
        display: "flex",
        backgroundColor: "#55d6aa"
    },
    navLink: {
        color: "#444",
    },
    mobileNavLink: {
        color: tokens.colorBrandForegroundLink
    },
    activeNavLink: {
        color: "#444",
        ...shorthands.borderBottom("3px", "solid", "white"),
        ...shorthands.padding("5px")
    },
    mobileActiveNavLink: {
        color: tokens.colorBrandForegroundLink,
        ...shorthands.borderBottom("3px", "solid", "white"),
        ...shorthands.padding("5px")
    },
    pendingNavLink: {
        backgroundColor: "lightgray",
        ...shorthands.padding("5px")
    },
    userIcon: {
        marginLeft: "20px",
        cursor: "pointer",
        display: "inline-block"
    },
    mobileHamburger: {
        float: "right",
        marginTop: "15px"
    },
    navList: {
        listStyle: "none"
    },
    loginLink: {
        listStyle: "none",
        fontWeight: 800,
        color: "#444"
    },
    mobileLoginLink: {
        listStyle: "none",
        color: tokens.colorBrandForegroundLink
    },
    mobileNav: {
        ...formStyle,
        padding: "10px"
    },
    form: formStyle,
    formInput: formInputStyle,
    formInputInline: formInputInlineStyle,
    infoLabel: infoLabel
});

export const Navbar: React.FC<any> = () => {
    const styles = useStyles();
    const location = useLocation();
    const isMobile = useMediaQuery(mobileMaxWidth);
    const pathName = React.useRef(location.pathname);

    const [registrationDialogVisible, setRegistrationDialogVisible] = React.useState(false);

    const [expectedUserEtag, setExpectedUserEtag] = React.useState<string | null>(null);

    const [userSettingsDialogVisible, setUserSettingsDialogVisible] = React.useState(false);

    const [policiesAccepted, setPoliciesAccepted] = React.useState(false);

    const [globalState, globalDispatch] = useGlobalContext();

    const [popupOpen, setPopupOpen] = React.useState(false);

    const checkBoxId = useId("acceptance-checkbox");

    const fetchPublishers = React.useCallback(async () =>
    {
        if (globalState.auth?.userDetails && !globalState.publishers?.length)
        {
            const response = await fetch("/api/publishers");

            if (response.ok)
            {
                const body = await response.json() as RetrieveMultipleResponse<Publisher>;

                globalDispatch({ type: "setPublishers", payload: body.records });
            }
        }
    }, [globalDispatch, globalState.auth?.userDetails, globalState.publishers?.length]);

    const fetchUser = React.useCallback(async () => {
        if (globalState.auth?.userDetails)
        {
            const response = await fetch("/api/users", {
                method: "GET",
                headers: {
                    "Content-Type": "application/json"
                }
            });
            
            if (response.ok)
            {
                const user = await response.json() as UserRecord;
                globalDispatch({ type: "setUser", payload: user });

                return user._etag;
            }
            else if (response.status === 404 && pathName.current != null && !pathName.current.startsWith("/legal"))
            {
                setRegistrationDialogVisible(true);
            }
        }

        return null;
    }, [globalDispatch, globalState.auth?.userDetails]);

    const confirmPolicies = React.useCallback(async () =>
    {
        try
        {
            const userResponse = await fetch("/api/users", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ policiesAccepted: true })
            });

            await ensureSuccess(userResponse);

            const user = await userResponse.json();
            globalDispatch({ type: "setUser", payload: user });
            globalDispatch({ type: "setPublishers", payload: [] });

            setRegistrationDialogVisible(false);
        }
        catch(e)
        {
            handleError(e as Error, globalDispatch);
        }
    }, [globalDispatch]);

    const logout = React.useCallback(() =>
    {
        window.location.href = `/.auth/logout?post_logout_redirect_uri=/`;
    }, []);

    const showUserSettingsDialog = React.useCallback(async () => {
        setPopupOpen(false);

        try {
            const etag = await fetchUser();

            if (etag) {
                setExpectedUserEtag(etag);
            }
        } catch (e) {
            handleError(e as Error, globalDispatch);
        }
    }, [fetchUser, globalDispatch]);

    const hideUserSettingsDialog = React.useCallback(() =>
    {
        setUserSettingsDialogVisible(false);
    }, []);

    const handleOpenChange = React.useCallback((e: OpenPopoverEvents, data: OnOpenChangeData) =>
    {
        setPopupOpen(data.open || false);
    }, []);

    React.useEffect(() => { fetchUser() }, [fetchUser]);

    React.useEffect(() => {
        pathName.current = location.pathname;
    }, [location.pathname]);

    React.useEffect(() => {
        if (expectedUserEtag != null && globalState.user?._etag === expectedUserEtag) {
            setUserSettingsDialogVisible(true);
            setExpectedUserEtag(null);
        }
    }, [expectedUserEtag, globalState.user?._etag]);

    React.useEffect(() => { fetchPublishers() }, [fetchPublishers]);

    React.useEffect(() => {
        (async () => {
            const userInfo = await getUserInfo();

            globalDispatch({ type: "setAuth", payload: userInfo });
        })();
    }, [globalDispatch]);

    const redirect = window.location.pathname;

    const defaultNavbar = React.useMemo(() =>
    {
        return (
            <>
                { !!globalState.user?.guid &&<li className={styles.navList}><NavLink end={false} className={({ isActive }) => isMobile ? (isActive ? styles.mobileActiveNavLink : styles.mobileNavLink) : (isActive ? styles.activeNavLink : styles.navLink)} to="/workspace">Workspace</NavLink></li> }
                <li className={styles.navList}><NavLink end={false} className={({ isActive }) => isMobile ? (isActive ? styles.mobileActiveNavLink : styles.mobileNavLink) : (isActive ? styles.activeNavLink : styles.navLink)} to="/docs">Docs</NavLink></li>
                <li className={styles.navList}><NavLink end={false} className={({ isActive }) => isMobile ? (isActive ? styles.mobileActiveNavLink : styles.mobileNavLink) : (isActive ? styles.activeNavLink : styles.navLink)} to="/legal">Legal</NavLink></li>
                { !globalState.user?.guid && <li className={styles.navList}><a className={isMobile ? styles.mobileLoginLink : styles.loginLink} href={`/.auth/login/aad?post_login_redirect_uri=${redirect}`}>Login</a></li> }
                { !!globalState.user?.guid && (
                    <Popover withArrow open={popupOpen} onOpenChange={handleOpenChange}>
                        <PopoverTrigger disableButtonEnhancement>
                            <Persona
                                className={styles.userIcon}
                                size="medium"
                                name={globalState.user.emailAddress}
                                title={globalState.user.emailAddress}
                                avatar={{ color: "neutral", style: { marginRight: "0px" } }}
                                primaryText=""
                            />
                        </PopoverTrigger>
                    
                        <PopoverSurface tabIndex={-1}>
                            <div className={styles.form}>
                                <Button onClick={showUserSettingsDialog}>Settings</Button>
                                <Button onClick={logout}>Logout</Button>
                            </div>
                        </PopoverSurface>
                    </Popover>
                )}
            </>
        );
    }, [isMobile, globalState.user?.guid, globalState.user?.emailAddress, styles.navList, styles.navLink, styles.loginLink, styles.mobileNavLink, styles.mobileActiveNavLink, styles.mobileLoginLink, styles.userIcon, styles.form, styles.activeNavLink, redirect, popupOpen, handleOpenChange, showUserSettingsDialog, logout]);    

    const mobileNavbar = React.useMemo(() => 
    {
        return (
            <Popover>
                <PopoverTrigger disableButtonEnhancement>
                    <MenuButton className={styles.mobileHamburger} icon={<Hamburger />} />
                </PopoverTrigger>
            
                <PopoverSurface tabIndex={-1}>
                    <div className={styles.mobileNav}>
                        { defaultNavbar }
                    </div>
                </PopoverSurface>
            </Popover>
        );
    }, [defaultNavbar, styles.mobileHamburger, styles.mobileNav]);

    const desktopNavbar = React.useMemo(() =>
    {
        return (
            <nav>
                <ul className={styles.navList}>
                    { defaultNavbar }
                </ul>
            </nav>
        );
    }, [defaultNavbar, styles.navList]);

    return (
        <header className={styles.header}>
            <FluentDialog title={`Welcome ${globalState.auth?.userDetails}`} open={registrationDialogVisible} submitDisabled={!policiesAccepted} onSubmit={confirmPolicies} onCancel={logout}>
                <div className={styles.form}>
                    <Text>
                        To create your user profile, please read our <FluentLink href='/legal/termsandconditions' target="_blank">Terms and Conditions</FluentLink>, <FluentLink href='/legal/acceptableusepolicy' target="_blank">Acceptable Use Policy</FluentLink>, <FluentLink href='/legal/privacypolicy' target="_blank">Privacy Policy</FluentLink>, <FluentLink href='/legal/cookiepolicy' target="_blank">Cookie Policy</FluentLink>, <FluentLink href='/legal/returnpolicy' target="_blank">Return Policy</FluentLink> and <FluentLink href='/legal/disclaimer' target="_blank">Disclaimer</FluentLink>.
                        If you do not agree, we won’t be able to create your user profile, and you’ll be logged out and redirected to the landing page.
                    </Text>
                    <div className={styles.formInputInline}>
                        <Label htmlFor={checkBoxId} size="medium">
                            I have read and agree to the Terms and Conditions, Acceptable Use Policy, Privacy Policy, Cookie Policy, Return Policy and Disclaimer.
                        </Label>
                        <Checkbox id={checkBoxId} checked={policiesAccepted} onChange={(e, data) => setPoliciesAccepted(data.checked as boolean)} />
                    </div>
                </div>
            </FluentDialog>

            <UserSettingsDialog key={globalState.user?._etag} userSettingsDialogVisible={userSettingsDialogVisible} hideUserSettingsDialog={hideUserSettingsDialog} />

            <ErrorDialog />

            <div className={styles.root}>
                <div className="logo" style={{ height: "60px", display: "flex", flexDirection: "row", gap: "5px" }}>
                    <Link to="/"><img height="50px" alt="logo" src="/logo.png" style={{ padding: "5px" }}/></Link>
                    <span style={{ alignSelf: "center" }}><b>Ianus Guard</b></span>
                </div>

                { isMobile
                    ? mobileNavbar
                    : desktopNavbar
                }
                
            </div>
        </header>
    );
};